/**
 * StudentCompetitionBanner — баннер соревнования для конкретного ребёнка.
 *
 * Варианты:
 * - Не записан → «Запишитесь» + выбор предмета + кнопка
 * - Записан, pending → «Оплатите» + цена + кнопка
 * - Записан, paid → «Записан ✓» (зелёный)
 */
import React, { useState } from 'react';
import {
  Paper, Group, Box, Text, Button, Badge, Select,
} from '@mantine/core';
import { IconTrophy, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { cabinetService } from '../services/api';
import tokens from '../theme/designTokens';

const formatRub = (val) =>
  Number(val).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', timeZone: 'Europe/Moscow',
  });
};

const StudentCompetitionBanner = ({ student, competition, onUpdate, paymentsAvailable = true }) => {
  const [registering, setRegistering] = useState(false);
  const [paying, setPaying] = useState(false);
  const [subjectId, setSubjectId] = useState(null);
  const [showSubjectSelect, setShowSubjectSelect] = useState(false);

  if (!competition) return null;

  const participant = (competition.my_participants || []).find(
    (p) => p.student_id === student.id
  );
  const isRegistered = !!participant;
  const isPaid = participant?.payment_status === 'paid';
  const isPending = participant?.payment_status === 'pending';

  const subjectOptions = (competition.subjects || []).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await cabinetService.registerForCompetition(competition.id, {
        student_id: student.id,
        subject_id: subjectId ? Number(subjectId) : null,
      });
      notifications.show({
        title: 'Записано!',
        message: `${student.first_name} записан(а) на соревнование`,
        color: 'green',
      });
      setShowSubjectSelect(false);
      setSubjectId(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setRegistering(false);
    }
  };

  // ─── Paid ──────────────────────────────────────────────────────────
  if (isPaid) {
    return (
      <Paper
        p="md"
        radius="xl"
        style={{
          background: tokens.gradients.softGreen,
          border: `2px solid ${tokens.colors.brand.green}33`,
        }}
      >
        <Group gap="sm" align="center">
          <IconCheck size={18} color={tokens.colors.brand.green} stroke={3} />
          <Box>
            <Text size="sm" fw={800} style={{ color: tokens.colors.brand.green }}>
              Ждём вас на соревнованиях!
            </Text>
            <Text size="xs" c={tokens.colors.text.muted} fw={700}>
              {competition.name} — {formatDate(competition.event_date)}
            </Text>
          </Box>
          {participant.subject_name && (
            <Badge size="xs" variant="filled" color="green" radius="xl">
              {participant.subject_name}
            </Badge>
          )}
        </Group>
      </Paper>
    );
  }

  // ─── Pending (записан, ожидает оплаты) ─────────────────────────────
  if (isPending) {
    return (
      <Paper
        p="md"
        radius="xl"
        style={{
          background: 'linear-gradient(135deg, #FFF7ED, #FFE8CC)',
          border: `2px solid ${tokens.colors.brand.orange}33`,
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" align="center" style={{ flex: 1, minWidth: 0 }}>
            <IconTrophy size={20} color={tokens.colors.brand.orange} stroke={2.5} />
            <Box>
              <Text size="sm" fw={800} style={{ color: tokens.colors.text.primary }}>
                {competition.name}
              </Text>
              <Text size="xs" c={tokens.colors.text.muted} fw={700}>
                {formatDate(competition.event_date)}
              </Text>
            </Box>
            <Group gap={4} align="baseline">
              <Text fw={900} size="sm" style={{ color: tokens.colors.brand.orange }}>
                {formatRub(competition.price_with_discount)}
              </Text>
              {competition.discount_active && Number(competition.price) !== Number(competition.price_with_discount) && (
                <Text size="xs" fw={700} style={{ color: tokens.colors.text.muted, textDecoration: 'line-through' }}>
                  {formatRub(competition.price)}
                </Text>
              )}
            </Group>
          </Group>
          <Button
            size="xs"
            color={paymentsAvailable ? "brand-orange" : "gray"}
            radius={100}
            loading={paying}
            disabled={!paymentsAvailable}
            onClick={async () => {
              setPaying(true);
              try {
                const res = await cabinetService.payCompetition(
                  competition.id,
                  participant.id,
                );
                if (res.confirmation_url) {
                  window.location.href = res.confirmation_url;
                }
              } catch (err) {
                notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
                setPaying(false);
              }
            }}
          >
            Оплатить
          </Button>
        </Group>
      </Paper>
    );
  }

  // ─── Не записан ────────────────────────────────────────────────────
  return (
    <Paper
      p="md"
      radius="xl"
      style={{
        background: tokens.gradients.softPurple,
        border: `2px solid rgba(124,58,237,0.1)`,
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm" align="center" style={{ flex: 1, minWidth: 0 }}>
          <IconTrophy size={20} color={tokens.colors.brand.purple} stroke={2.5} />
          <Box>
            <Text size="sm" fw={800} style={{ color: tokens.colors.text.primary }}>
              {competition.name}
            </Text>
            <Text size="xs" c={tokens.colors.text.muted} fw={700}>
              {formatDate(competition.event_date)}
            </Text>
          </Box>
          {competition.price_with_discount && (
            <Group gap={4} align="baseline">
              <Text fw={900} size="sm" style={{ color: tokens.colors.brand.orange }}>
                {formatRub(competition.price_with_discount)}
              </Text>
              {competition.discount_active && Number(competition.price) !== Number(competition.price_with_discount) && (
                <Text size="xs" fw={700} style={{ color: tokens.colors.text.muted, textDecoration: 'line-through' }}>
                  {formatRub(competition.price)}
                </Text>
              )}
            </Group>
          )}
        </Group>

        {showSubjectSelect ? (
          <Group gap={4} wrap="nowrap">
            {subjectOptions.length > 0 && (
              <Select
                size="xs"
                placeholder="Предмет"
                data={subjectOptions}
                value={subjectId}
                onChange={setSubjectId}
                clearable
                radius="xl"
                style={{ width: 130 }}
              />
            )}
            <Button
              size="xs"
              color="brand-purple"
              radius={100}
              loading={registering}
              onClick={handleRegister}
            >
              Записать
            </Button>
            <Button
              size="xs"
              variant="subtle"
              radius={100}
              onClick={() => setShowSubjectSelect(false)}
            >
              ✕
            </Button>
          </Group>
        ) : (
          <Button
            size="xs"
            color="brand-purple"
            radius={100}
            onClick={() => setShowSubjectSelect(true)}
          >
            Записаться
          </Button>
        )}
      </Group>
    </Paper>
  );
};

export default StudentCompetitionBanner;
